import BigNumber from 'bignumber.js';
import { get } from 'lodash';

import { FIELD_PATHS, PII_FIELD_MASKING_CLASS } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { CompanyEntityIndex, CompanyOwner } from '@funnels/types';
import { getCompanyOwnershipPercentage, shouldSkipValidate } from '@funnels/utils';

import { Suffix } from '../../Common';
import TextInputField from '../../Fields/_uncontrolled/TextInputField';
import HiddenField from '../../Fields/HiddenField';

interface BusinessOwnershipProps extends CompanyEntityIndex {
  applicantId: string;
  isOptional?: boolean;
  isDisabled?: boolean;
}

const isTotalOwnershipValid = (companyOwners: CompanyOwner[]) =>
  companyOwners.reduce((pre, curr) => pre + (curr.percentOwned || 0), 0) <= 1;

export const BusinessOwnership = (props: BusinessOwnershipProps) => {
  const { companyIndex, applicantId, isOptional, isDisabled } = props;
  const [formValues, setFormValue] = useFormValues();
  const companyOwners: CompanyOwner[] = get(formValues, FIELD_PATHS.COMPANY_OWNERS(companyIndex), []);
  const applicantPercentOwned = getCompanyOwnershipPercentage(companyOwners, applicantId);

  return (
    <>
      <TextInputField
        label="Ownership percentage"
        isDisabled={isDisabled}
        value={applicantPercentOwned.toString()}
        isFullWidth
        after={<Suffix>%</Suffix>}
        onChange={(value) => {
          const otherOwnerships = companyOwners.filter((ownership) => ownership.applicantId !== applicantId);
          if (!value) {
            setFormValue(
              FIELD_PATHS.COMPANY_OWNERS(companyIndex),
              otherOwnerships.length ? otherOwnerships : undefined
            );
          } else if (!!parseFloat(value)) {
            const applicantOwnership: CompanyOwner = {
              applicantId,
              percentOwned: new BigNumber(value).dividedBy(100).toNumber(),
            };
            setFormValue(FIELD_PATHS.COMPANY_OWNERS(companyIndex), [...otherOwnerships, applicantOwnership]);
          }
        }}
      />
      <HiddenField
        name={FIELD_PATHS.COMPANY_OWNERS(companyIndex)}
        validate={(value: CompanyOwner[] = []) => {
          const ownership = getCompanyOwnershipPercentage(value, applicantId);
          if (shouldSkipValidate(ownership, isOptional)) return;
          if (!ownership) {
            return 'Please enter the ownership percentage';
          }
          if (ownership > 100) {
            return 'Ownership percentage cannot be greater than 100%';
          }
          if (ownership === 0) {
            return 'Ownership percentage cannot be 0%';
          }
          if (!isTotalOwnershipValid(value)) {
            return 'Total ownership percentage cannot exceed 100%';
          }
        }}
      />
    </>
  );
};
